import axios from "axios";
import showToast from "common/Notification";
import { fullDateFormat } from "constants/TaskConstants";
import { capitalize, isEmpty } from "lodash";
import moment from "moment";
import { put } from "redux-saga/effects";

/**
 * Converts a list of files into FormData and attaches it to an instance.
 *
 * @param {Array} list - Array of files or subarrays of files.
 * @param {FormData} instance - FormData instance to append files to.
 * @param {string} name - Name to use as a prefix for the file keys in FormData.
 * @param {string} checkVariableName - Name to use as a prefix for the file check key in FormData.
 * @returns {FormData} - The FormData instance with attached files.
 */
export const fileListToFormData = (list = [], instance, name = "", checkVariableName = "attachment") => {
  const attach = list.flat();

  attach.forEach((item, ind) => {
    instance?.set(`${name}[${ind}]`, item);
  });

  instance?.set(checkVariableName, isEmpty(list) ? "0" : "1");
  return instance;
};

/**
 * Converts an object into FormData, handling arrays by converting them to JSON strings.
 *
 * @param {Object} obj - Object to be converted to FormData.
 * @param {FormData} instance - FormData instance to append the object to.
 * @returns {FormData} - The FormData instance with appended key-value pairs.
 */
export const objectToFormData = (obj = {}, instance) => {
  Object.entries(obj).forEach(([key, value]) => {
    console.log("key", key, "value", value);
    instance?.append(key, Array.isArray(value) ? JSON.stringify(value) : value);
  });
  return instance;
};

/**
 * Downloads a file from a given link.
 *
 * @param {string} Link - URL of the file to download.
 * @returns {Promise<void>} - A promise that resolves when the download is complete.
 */
export const downloadFile = async Link => {
  try {
    const res = await axios({
      url: `${process.env.REACT_APP_APIBASE}/${Link}`,
      method: "GET",
      responseType: "blob",
    });

    const url = window.URL.createObjectURL(new Blob([res.data]));
    const dlink = document.createElement("a");
    dlink.href = url;
    dlink.setAttribute("download", Link.slice(14));
    document.body.appendChild(dlink);
    dlink.click();
    dlink.remove();
  } catch (e) {
    showToast("error", "Downloading File", e);
    console.error(e);
  }
};

/**
 * Handles response in Redux Saga, triggers success/error callbacks, and shows notifications.
 *
 * @param {string} title - Title for notifications.
 * @param {Object} response - Response object.
 * @param {Function} successProvider - Function to be called on success (Redux action).
 * @param {Function} errorProvider - Function to be called on error (Redux action).
 * @param {Function} onSuccess - Additional success callback.
 * @param {Function} onError - Additional error callback.
 * @param {boolean} notifyOnSuccess - Whether to show success notifications.
 * @param {boolean} notifyOnError - Whether to show error notifications.
 */
export function* responseHandler(
  title = "",
  response = {},
  successProvider = () => {},
  errorProvider = () => {},
  onSuccess = () => {},
  onError = () => {},
  notifyOnSuccess = true,
  notifyOnError = true
) {
  if (response?.status) {
    if (successProvider) yield put(successProvider(response));
    if (onSuccess) onSuccess(response);
    if (notifyOnSuccess && response?.message) showToast("success", title, response?.message);
  } else {
    if (errorProvider) yield put(errorProvider(response));
    if (onError) onError(response);
    if (notifyOnError && response?.message) showToast("warning", title, response?.message);
  }
}

export const convertMinutesToHHMM = minutesInput => {
  const totalMinutes = parseInt(minutesInput, 10);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return `${hours}:${minutes.toString().padStart(2, "0")}`;
};

/**
 * Truncates a string to the specified length and adds ellipsis if necessary.
 *
 * @param {string} text - Text to be truncated.
 * @param {number} length - Maximum length of the truncated text.
 * @returns {string} - The truncated text with ellipsis if required.
 */
export const truncateText = (text = "", length = 0) => (text.length > length ? text.substring(0, length) + "..." : text);

/**
 * Formats duration as a string.
 *
 * @param {number} duration - Duration in hours as a decimal.
 * @param {boolean} numericForm - Whether to return numeric or verbose form.
 * @returns {string} - Formatted duration.
 */
export const formatDuration = (duration, numericForm) => {
  if (!duration) return "";

  const hours = Math.floor(duration);
  const minutes = Math.round((duration - hours) * 60);

  return numericForm ? `${hours}:${String(minutes).padStart(2, "0")}` : `${hours} Hour(s) ${minutes} Minute(s)`;
};

/**
 * Calculates the duration between two times.
 *
 * @param {string|Date} to - End time.
 * @param {string|Date} from - Start time.
 * @param {boolean} numericForm - Whether to return numeric or verbose form.
 * @returns {string} - Duration in hours and minutes.
 */
export const findDuration = (to, from, numericForm = false) => {
  if (to && from) {
    const diffMinutes = moment.duration(moment(to).diff(moment(from))).asMinutes();
    const hours = Math.floor(diffMinutes / 60);
    const minutes = Math.round(diffMinutes % 60);
    return numericForm ? `${hours}:${String(minutes).padStart(2, "0")}` : `${hours} Hour(s) ${minutes} Minute(s)`;
  }
  return "";
};

/**
 * Calculates age based on a date.
 *
 * @param {string|Date} date - Date to calculate age from.
 * @param {string} format - Format to display age ('y' for years, 'm' for months).
 * @returns {string} - Formatted age.
 */
export const calculateAge = (date, format = "y") => {
  if (!date) return "";

  const diff = moment().diff(moment(date), "milliseconds");
  const duration = moment.duration(diff);

  let text = duration.years() ? `${duration.years()} year${duration.years() > 1 ? "s" : ""} ` : "";

  if (format.includes("m")) {
    text += duration.months() ? `${duration.months()} month${duration.months() > 1 ? "s" : ""}` : `${duration.days()} day${duration.days() > 1 ? "s" : ""}`;
  }

  return text;
};

/**
 * Checks if a task's end time is valid based on the current time.
 *
 * @param {string} start - Start time of the task.
 * @param {string} end - End time of the task.
 * @returns {boolean} - True if the task end is valid, false otherwise.
 */
export const checkTaskValidity = (start, end) => {
  if (!start || !end) return false;

  const nowTime = moment().format(fullDateFormat);
  const afterEnd = moment(end, fullDateFormat).add(15, "minutes").format(fullDateFormat);

  return moment(nowTime, fullDateFormat).isAfter(afterEnd);
};

/**
 * Formats a name string by adding spaces between capital letters.
 *
 * @param {string} name - Name to format.
 * @returns {string} - Formatted name.
 */
export const formatName = name => (name ? capitalize(name.replace(/([A-Z])/g, " $1")) : "");

/**
 * Deep merges two objects, using the default object to fill missing keys.
 *
 * @param {Object} defaultObj - Default object structure.
 * @param {Object} mergerObj - Object to be merged with the default object.
 * @returns {Object} - Merged object.
 */
export function deepMergeWithDefaults(defaultObj, mergerObj) {
  Object.keys(defaultObj).forEach(key => {
    if (typeof defaultObj[key] === "object" && defaultObj[key] !== null && !Array.isArray(defaultObj[key])) {
      mergerObj[key] = mergerObj[key] || {};
      deepMergeWithDefaults(defaultObj[key], mergerObj[key]);
    } else if (!(key in mergerObj)) {
      mergerObj[key] = defaultObj[key];
    }
  });
  return mergerObj;
}

export function dataURLtoFile(dataUrl, filename) {
  const [header, base64Data] = dataUrl.split(",");
  const mime = header.match(/:(.*?);/)[1];

  const binary = atob(base64Data);
  const array = [];
  for (let i = 0; i < binary.length; i++) {
    array.push(binary.charCodeAt(i));
  }

  return new File([new Uint8Array(array)], filename, { type: mime });
}

export const getValue = (field, obj) => field?.split(".").reduce((acc, key) => acc && acc[key], obj) || "";
